import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchModule, SearchComponent, SearchState, SearchService } from '@nx-ahs/search';
import { NotificationService } from '@nx-ahs/shared/services';
import { IAppNotification } from '@nx-ahs/shared/interfaces';
import {
	ConfirmationModalService,
	PageHeaderComponent,
	AppSpinnerComponent,
	MenuComponent
} from '@nx-ahs/shared/components';
import { PermissionIdDirective } from '@nx-ahs/shared/directives';
import { ApplicationModel, AppMenuItem } from '@nx-ahs/shared/models';
import { Subscription } from 'rxjs';
import { MailService } from '../services/mail.service';
import { MailDialogService } from '../services/mail-dialog.service';
import moment from 'moment';

@UntilDestroy()
@Component({
	selector: 'ahs-mail-console',
	templateUrl: './mail-console.component.html',
	styleUrls: ['./mail-console.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		FontAwesomeModule,
		SearchModule,
		PageHeaderComponent,
		AppSpinnerComponent,
		MenuComponent,
		PermissionIdDirective
	]
})
export class MailConsoleComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild('mailConsoleSearch') mailConsoleSearch: SearchComponent;

	//#region Public Members
	public breadcrumbs: string;
	public loading: boolean = false;
	public mailConsoleMenuConfig: AppMenuItem[] = [];
	public showApprove: boolean = false;
	public showMarkSubcontracted = false;
	public showMarkPrinted: boolean = false;
	public showMarkSent: boolean = false;
	public showViewCoverpage: boolean = false;
	public showViewUSPSPaperwork: boolean = false;
	public searchCustomizedSubscription: Subscription;
	public pageTitle: string = 'Mail Console';
	public showEditBatchNotes: boolean = false;
	public showEditBatch: boolean = false;
	//#endregion Public Members

	//#region Private Members
	private app: ApplicationModel;
	private appSubscription: Subscription;
	private currentMenuItem: AppMenuItem;
	private mailConsolePromise: Promise<any>;
	private newMailBatchNotifications: IAppNotification[] = [];
	//#endregion Private Members

	constructor(
		public mailService: MailService,
		private mailDialogService: MailDialogService,
		@Inject('securityService') private securityService: any,
		private confirmationModalService: ConfirmationModalService,
		private searchService: SearchService,
		private notificationService: NotificationService
	) {}

	ngOnInit(): void {
		// Subscribe to mail changes
		this.mailService.mailBatchChangedEvent.pipe(untilDestroyed(this)).subscribe((mailBatchIds: number[]) => {
			// Refresh the counts
			this.refreshMailConsoleCounts();

			// If the current search has results then determine if we need to re-run the search
			if (this.mailConsoleSearch.searchState && this.mailConsoleSearch.searchState.results) {
				this.checkGridForMailBatchesAndUpdate(mailBatchIds);
			}
		});

		this.mailService.newMailBatchesNotificationEvent.pipe(untilDestroyed(this)).subscribe((notifications: any) => {
			if (this.securityService.getCurrentApp().application.applicationId == notifications[0]?.appId) {
				// Refresh the counts
				this.refreshMailConsoleCounts();
				notifications.forEach((notification) => {
					const existingUnreadNotification = this.newMailBatchNotifications.some(
						(n) => n.subject === notification.subject && !n.isRead
					);
					if (!existingUnreadNotification) {
						const newNotification = this.notificationService.addNotification(
							notification.subject,
							notification.message,
							moment(),
							true
						);
						this.newMailBatchNotifications.push(newNotification);
					}
				});
			}
		});

		this.notificationService.notificationsUpdated
			.pipe(untilDestroyed(this))
			.subscribe((notifications: IAppNotification[]) => {
				for (let index = 0; index < this.newMailBatchNotifications.length; index++) {
					const notification = this.newMailBatchNotifications[index];
					const notificationExists = notifications.some(
						(n) => n.subject == notification.subject && n.message == notification.message
					);
					if (!notificationExists) {
						console.log(notification);
						this.newMailBatchNotifications.splice(index, 1);
					}
				}
			});

		// Subscribe to custom view changes
		this.searchCustomizedSubscription = this.searchService.customViewSaved.subscribe((newState) => {
			this.mailConsoleSearch.searchState.displayedColumns = this.mailConsoleSearch.createDisplayedColumns(
				this.mailConsoleSearch.searchState.definition,
				newState.state
			);
		});

		// Subscribe to app changes since client config is needed for inbox setup
		this.appSubscription = this.securityService.appChanged.pipe(untilDestroyed(this)).subscribe((app) => {
			this.app = app;

			if (this.app) {
				this.mailConsolePromise = this.loadMailOutboxes();
			}
		});

		// If the app already exists then store it and load the inboxes
		if (this.securityService.getCurrentApp()) {
			this.app = this.securityService.getCurrentApp();
			this.mailConsolePromise = this.loadMailOutboxes();
		}
	}

	ngOnDestroy(): void {}

	ngAfterViewInit(): void {
		this.mailConsolePromise.then(() => {});
	}

	//#region Public Methods
	public getPreparedBatches(mi: AppMenuItem): void {
		// If the current menu item was clicked again then exit method, otherwise update current menu item
		if (mi == this.currentMenuItem) {
			return;
		} else {
			this.currentMenuItem = mi;
		}

		this.showApprove = this.currentMenuItem.metadata?.isApprovalOutbox && this.securityService.hasPermission(1100);
		this.showMarkSubcontracted =
			!this.currentMenuItem.metadata?.isApprovalOutbox &&
			!this.currentMenuItem.metadata?.isSubcontractedOutbox &&
			this.securityService.hasPermission(4258);
		this.showMarkPrinted =
			!this.currentMenuItem.metadata?.isApprovalOutbox && this.securityService.hasPermission(1101);
		this.showMarkSent =
			!this.currentMenuItem.metadata?.isApprovalOutbox && this.securityService.hasPermission(1102);

		// Set the search data
		this.mailConsoleSearch.searchId = mi.metadata.searchId;
		this.mailConsoleSearch.criteriaValues = mi.metadata;
		this.mailConsoleSearch.customizeKey = 'mail-console-' + this.currentMenuItem.indexId;

		// Create search state if it doesn't exist, otherwise reset the search
		if (!this.mailConsoleSearch.searchState) {
			this.mailConsoleSearch.searchState = new SearchState();
		} else {
			this.mailConsoleSearch.resetState();
		}

		// Set the criteria expansion state
		this.mailConsoleSearch.searchState.criteriaExpanded = mi.metadata.criteriaExpanded === true;

		// Tell search control to updated search
		this.mailConsoleSearch.loadSearch(true).then(() => {
			// Should the search be run automatically
			const autoRun = mi.metadata.preventAutoRun !== true;

			// Perform the search
			if (autoRun) {
				this.mailConsoleSearch.search(false).then(() => {
					// Update menu item count when search is finished
					mi.count = this.mailConsoleSearch.searchState.totalRows;
				});
			}
		});

		// Update breadcrums for top of page
		//this.breadcrumbs = this.getBreadcrumbs(mi);
		this.pageTitle = this.getTitle(mi);
	}

	public markMailBatchesApproved() {
		this.confirmationModalService
			.showConfirmationDialog('Confirm Action', 'Are you sure want to approve these mail batches?')
			.afterClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					if (this.loading == false) {
						this.loading = true;

						const mailBatchIds = this.mailConsoleSearch.searchState.selectedRows.selected.map((x) => {
							return x.mailBatchId;
						});
						this.mailService.markMailBatchesApproved(mailBatchIds).then(() => {
							this.loading = false;
						});
					}
				}
			});
	}

	public markMailBatchesPrinted() {
		this.confirmationModalService
			.showConfirmationDialog('Confirm Action', 'Are you sure want to mark these mail batches as printed?')
			.afterClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					if (this.loading == false) {
						this.loading = true;

						const mailBatchIds = this.mailConsoleSearch.searchState.selectedRows.selected.map((x) => {
							return x.mailBatchId;
						});
						this.mailService.markMailBatchesPrinted(mailBatchIds).then(() => {
							this.loading = false;
						});
					}
				}
			});
	}

	public markMailBatchesSent() {
		this.confirmationModalService
			.showConfirmationDialog('Confirm Action', 'Are you sure want to mark these mail batches as sent?')
			.afterClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					if (this.loading == false) {
						this.loading = true;

						const mailBatchIds = this.mailConsoleSearch.searchState.selectedRows.selected.map((x) => {
							return x.mailBatchId;
						});
						this.mailService.markMailBatchesSent(mailBatchIds).then(() => {
							this.loading = false;
						});
					}
				}
			});
	}

	public markMailBatchesSubcontracted() {
		this.confirmationModalService
			.showConfirmationDialog('Confirm Action', 'Are you sure want to mark these mail batches as sub-contracted?')
			.afterClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					if (this.loading == false) {
						this.loading = true;

						const mailBatchIds = this.mailConsoleSearch.searchState.selectedRows.selected.map((x) => {
							return x.mailBatchId;
						});
						this.mailService.markMailBatchesSubcontracted(mailBatchIds).then(() => {
							this.loading = false;
						});
					}
				}
			});
	}

	public unprepareMailBatches() {
		let unprepareReasonId: number = 2;
		if (this.currentMenuItem.metadata?.isApprovalOutbox)
			//ApprovalOutbox will be true in MAIL.MailOutbox for "Batches Ready for Approval" or "E-Notices Ready for Approval"
			unprepareReasonId = 2;
		//ApprovalOutbox will be false in MAIL.MailOutbox for "Prepared Batches"
		else unprepareReasonId = 3;

		this.confirmationModalService
			.showConfirmationDialog('Confirm Action', 'Are you sure want to discard these mail batches?')
			.afterClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					if (this.loading == false) {
						this.loading = true;

						const mailBatchIds = this.mailConsoleSearch.searchState.selectedRows.selected.map((x) => {
							return x.mailBatchId;
						});
						this.mailService.unprepareMailBatches(mailBatchIds, unprepareReasonId).then(() => {
							this.loading = false;
						});
					}
				}
			});
	}
	public editBatchNotes(): void {
		const mailBatchId = this.mailConsoleSearch.searchState.selectedRows.selected[0]?.mailBatchId;
		if (mailBatchId) {
			this.mailDialogService.editBatchNotes(mailBatchId);
		}
	}

	public editBatch(): void {
		const mailBatchId = this.mailConsoleSearch.searchState.selectedRows.selected[0]?.mailBatchId;
		if (mailBatchId) {
			this.mailDialogService.editBatch(mailBatchId);
		}
	}

	//#endregion Public Methods

	//#region Private Methods
	private checkGridForMailBatchesAndUpdate(mailBatchIds: number[]) {
		const mailBatchFound = mailBatchIds.some((x) => {
			return this.mailConsoleSearch.searchState.results.data.some((y) => {
				return y.mailBatchId == x;
			});
		});

		if (mailBatchFound) {
			this.mailConsoleSearch.search();
		}
	}

	private createMenuItem(
		childMenu: any,
		iconId: any,
		metadata: any,
		permissionId: any,
		title: any,
		indexId: any
	): AppMenuItem {
		// Replace tokens in metadata if applicable
		if (metadata) {
			Object.keys(metadata).forEach((prop) => {
				if (metadata[prop]) {
					const matches = metadata[prop].toString().match(/\{([A-Za-z0-9]*)\}/g);
					if (matches) {
						matches.forEach((m) => {
							metadata[prop] = this[metadata[prop].toString().replace('{', '').replace('}', '')];
						});
					}
				}
			});
		}

		const menuItem: AppMenuItem = {
			childMenu: childMenu,
			disableCollapse: false,
			expanded: false,
			iconId: iconId,
			metadata: metadata,
			methodName: 'getPreparedBatches',
			permissionId: permissionId,
			title: title,
			url: null,
			alwaysEmit: true,
			indexId: indexId
		};

		return menuItem;
	}

	private getTitle(mi: AppMenuItem): string {
		return mi.title;
	}

	private async loadMailOutboxes(): Promise<any> {
		if (this.loading == false) {
			this.loading = true;

			this.mailConsoleMenuConfig = [];
			const outboxes = await this.mailService.getMailOutboxes().toPromise();
			outboxes.forEach((outbox) => {
				const mi = this.createMenuItem(
					null,
					outbox.iconId,
					{
						mailOutboxId: outbox.mailOutboxId,
						searchId: outbox.mailOutboxQueues[0].searchId,
						isApprovalOutbox: outbox.isApprovalOutbox,
						isENoticesOutbox: outbox.isENoticesOutbox,
						isSubcontractedOutbox: outbox.isSubcontractedOutbox
					},
					null,
					outbox.name,
					outbox.mailOutboxId + '.0'
				);

				const childMenu = this.loadMailOutboxQueueMenu(outbox);

				if (childMenu.length > 0) {
					mi.childMenu = childMenu;
				}

				this.mailConsoleMenuConfig.push(mi);
			});

			await this.refreshMailConsoleCounts();

			this.loading = false;
		}
	}

	private loadMailOutboxQueueMenu(mailOutbox: any): AppMenuItem[] {
		const menu: AppMenuItem[] = [];

		mailOutbox.mailOutboxQueues.forEach((queue) => {
			const mi = this.createMenuItem(
				null,
				queue.iconId,
				{
					mailOutboxId: mailOutbox.mailOutboxId,
					mailOutboxQueueId: queue.mailOutboxQueueId,
					searchId: queue.searchId,
					isApprovalOutbox: mailOutbox.isApprovalOutbox,
					isENoticesOutbox: mailOutbox.isENoticesOutbox,
					isSubcontractedOutbox: mailOutbox.isSubcontractedOutbox
				},
				null,
				queue.name,
				mailOutbox.mailOutboxId + '.' + queue.mailOutboxQueueId
			);

			let childMenu: AppMenuItem[] = [];
			if (queue.mailOutboxTypes.length > 0) {
				childMenu = this.loadMailOutboxTypeMenu(
					queue,
					mailOutbox.mailOutboxId,
					queue.searchId,
					mailOutbox.isApprovalOutbox,
					mailOutbox.isENoticesOutbox,
					mailOutbox.isSubcontractedOutbox
				);
			}

			if (childMenu.length > 0) {
				mi.childMenu = childMenu;
			}

			menu.push(mi);
		});

		return menu;
	}

	private loadMailOutboxTypeMenu(
		mailOutboxQueue: any,
		mailOutboxId: number,
		searchId: number,
		isApprovalOutbox: boolean,
		isENoticesOutbox: boolean,
		isSubcontractedOutbox: boolean
	): AppMenuItem[] {
		const menu: AppMenuItem[] = [];

		// Loop through each Task Type
		mailOutboxQueue.mailOutboxTypes.forEach((type) => {
			// Create menu item for Task Type
			const mi = this.createMenuItem(
				null,
				type.iconId,
				{
					mailOutboxId: mailOutboxId,
					mailOutboxQueueId: mailOutboxQueue.mailOutboxQueueId,
					mailOutboxTypeId: type.mailOutboxTypeId,
					searchId: mailOutboxQueue.searchId,
					isApprovalOutbox: isApprovalOutbox,
					isENoticesOutbox: isENoticesOutbox,
					isSubcontractedOutbox: isSubcontractedOutbox
				},
				type.permissionId,
				type.name,
				mailOutboxId + '.' + mailOutboxQueue.mailOutboxQueueId + '.' + type.mailOutboxTypeId
			);

			// Add Task Type item to menu
			menu.push(mi);
		});

		return menu;
	}

	private async refreshMailConsoleCounts(): Promise<void> {
		const counts = await this.mailService.getMailOutboxCounts().toPromise();
		counts.forEach((i) => {
			this.updateMenuItem(this.mailConsoleMenuConfig, i);
		});
	}

	private updateMenuItem(menu: any, i: any): void {
		const mi = menu.find((x) => x.indexId == i.id);

		if (mi) {
			mi.count = i.count;
		} else {
			menu.forEach((m) => {
				if (m.childMenu) {
					this.updateMenuItem(m.childMenu, i);
				}
			});
		}
	}
	//#endregion Private Methods
}
